import * as React from "react";
import { TextWithTooltip } from "@/components/shared/tooltip/TextWithTooltip";

type TickCompProps = {
  x?: number;
  y?: number;
  payload?: { value: string | number };
  axis?: "x" | "y";
  // computed from parent
  slotWidth: number; // usable pixel width per tick
  pad?: number; // inner padding for aesthetics
  dy?: number;
  dx?: number;
  anchor?: "start" | "middle" | "end";
  labelFormatter?: (value: string) => React.ReactNode;
};

export function TruncatedTick({
  x = 0,
  y = 0,
  payload,
  axis = "x",
  slotWidth,
  pad = 4,
  dy = 0,
  dx = 0,
  anchor = "middle",
  labelFormatter,
}: TickCompProps) {
  if (!payload) return null;
  const value = String(payload.value);

  const cx = x + dx;
  const cy = y + dy;

  // clamp to a minimum so <foreignObject> renders reliably
  const w = Math.max(1, slotWidth);
  const h = 18; // ~1 line of text; Radix size="1" is ~12–13px

  // anchor horizontally
  let fx = cx - (anchor === "middle" ? w / 2 : anchor === "end" ? w : 0);
  // place below axis line for X, vertically centered for Y
  let fy = axis === "x" ? cy + 2 : cy - h / 2;

  return (
    <g>
      <foreignObject x={fx} y={fy} width={w} height={h} overflow="visible">
        <div
          style={{ width: w, height: h }}
          className={axis === "x" ? "flex items-center justify-center" : ""}
        >
          <TextWithTooltip
            size="1"
            as="span"
            style={{
              display: "inline-block",
              maxWidth: w - pad * 2,
              paddingInline: pad,
            }}
            truncate
            alignOffset={0}
            sideOffset={-20}
          >
            {labelFormatter?.(value) ?? value}
          </TextWithTooltip>
        </div>
      </foreignObject>
    </g>
  );
}
